import type { MarketScope, OverUnder } from "@/components/optimizer/query-state";
import { SOON_HOURS, coerceGameDays } from "@/lib/game-date";

export type BookSelection = readonly string[] | null;
export type GameWindow = { readonly leagues: readonly string[]; readonly days: readonly string[]; readonly within: number };

const MARKET_SCOPES: ReadonlySet<unknown> = new Set<MarketScope>(["", "player", "game"]);
const OVER_UNDER: ReadonlySet<unknown> = new Set<OverUnder>(["", "over", "under"]);

export function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string" && item.length > 0);
}

export function isBounded(value: unknown, max: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= max;
}

export function isBookSelection(value: unknown): value is string[] | null {
  return value === null || isStringList(value);
}

export function isMarketScope(value: unknown): value is MarketScope {
  return MARKET_SCOPES.has(value);
}

export function isOverUnder(value: unknown): value is OverUnder {
  return OVER_UNDER.has(value);
}

export function savedFilterState(value: unknown, version: number): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const outer = value as Record<string, unknown>;
  if (!outer.state || typeof outer.state !== "object" || Array.isArray(outer.state)) return null;
  const raw = outer.state as Record<string, unknown>;
  return raw.version === version ? raw : null;
}

export function coerceGameWindow(raw: Record<string, unknown>): GameWindow | null {
  if (!isStringList(raw.leagues)) return null;
  if (!Array.isArray(raw.days) || coerceGameDays(raw.days).length !== raw.days.length) return null;
  if (raw.within !== 0 && !SOON_HOURS.some((hours) => hours === raw.within)) return null;
  return { leagues: raw.leagues, days: coerceGameDays(raw.days), within: raw.within as number };
}
